import { inject } from '@angular/core';
import {
  ContentConnector,
  ContextMenuOption,
  LoadTaskResult,
  MenuType,
  ShapeGroupOrderState,
} from '../team-apis/content/content-connector';
import { ViewBox } from '../team-apis/content/view-box';
import {
  EventTrackingValue,
  KeyboardEventTrackingValue,
} from '../team-apis/core/event-tracking';
import { TRAINING_INDEX } from '../team-apis/core/reserved-shape-indexes';
import { Answer } from '../team-apis/otterdam/answers/answer';
import {
  PointType,
  ShapeLocation,
  ShapeType,
} from '../team-apis/otterdam/answers/shape-typings';
import { Task } from '../team-apis/otterdam/task';
import { AnnotationTransientState } from './annotation/shared/state/annotation-state';
import { ShapeState } from './annotation/shared/state/shape-state';
import { KeyboardShortcutService } from './misc-services/keyboard-shortcut.service';
import { EventTrackingValueWithSource } from './misc-utils';
import { ShapeFilter } from './state/abstract-sensor-content-state';
import { AbstractSensorContentStore } from './state/abstract-sensor-content-store';
import { ToolName } from './tools-names';

export abstract class AbstractContentConnector<
  P extends PointType,
  S extends ShapeType,
  L extends ShapeLocation<P> = ShapeLocation<P>,
> implements ContentConnector<P, S, L>
{
  private keyboardShortcutService = inject(KeyboardShortcutService);

  protected abstract store: AbstractSensorContentStore;

  handleKeyboardShortcut(
    shortcutName: string,
    keyboardEvent: KeyboardEvent,
    eventTrackingValue: KeyboardEventTrackingValue,
  ) {
    this.keyboardShortcutService.keyboardShortcutEvent$.next({
      shortcutName,
      keyboardEvent,
      eventTrackingValue,
    });
  }

  disableFeedbackCreation() {
    this.store('isShapeFeedbackEditable').set(false);
  }

  async inspectFocusedShape(_maintainZoom: boolean) {
    throw new Error('Unimplemented inspectFocusedShape');
  }

  /** @deprecated used for old projects without objects */
  async prepareForSave(_isDraft: boolean): Promise<Answer | undefined> {
    return undefined;
  }

  onAnnotationFocusChange(_hasFocus: boolean) {}

  setFeedbackMarkers(_points: P[]) {}

  updateExtrapolation(
    _shapeIndex: number,
    _frameNumber: number,
  ): number | undefined {
    return undefined;
  }

  setIsZoomPanelOpen(_isOpen: boolean): void {}

  updateShapeCreationEnabled(scopedObjectId: number | undefined) {
    const { project } = this.store.state();
    this.store('shapeCreationEnabled').set(
      scopedObjectId !== undefined || !project.object_creation_off,
    );
  }

  handleViewBoxPaddingUpdate(viewBoxPadding?: number) {
    this.store('viewBoxPadding').set(viewBoxPadding);
  }

  protected convertToTrainingShapes<T extends ShapeState>(shapes: T[]) {
    return shapes.reduce<StrictRecord<number, T>>((acc, shape) => {
      acc[shape.index] = { ...shape, index: TRAINING_INDEX };
      return acc;
    }, {});
  }

  abstract canRedo(): boolean;

  abstract canUndo(): boolean;

  abstract deactivateSelectedTool(): void;

  abstract activateSelectedTool(): Promise<void>;

  abstract setFrameNumber(frameNumber: number): void;

  abstract setShapeFilters(filters: ShapeFilter[]): void;

  abstract setShapeTransientInfo(
    shapeTransientInfo: StrictRecord<number, AnnotationTransientState>,
  ): void;

  abstract setShapes({
    shapes,
    groups,
    shapeAndGroupOrder,
  }: ShapeGroupOrderState): void;

  abstract showTrainingShapes(shapes: StrictRecord<number, ShapeState>): void;

  abstract inspectFeedbackMarker(position: P): Promise<void>;

  abstract isOutputBlank(): boolean;

  abstract determineLocation(frameNumberStore: number, shapeIndex: number): L;

  abstract ensureKeyLocation(shapeIndex: number, frameNumber: number): void;

  abstract getContextMenuOptions(
    menuType: MenuType,
  ): Promise<ContextMenuOption[]>;

  abstract pushCurrentState(options: {
    collectKey?: string;
    collectDebounce?: number;
  }): {
    hasTrackedChanges: boolean;
  };

  abstract redo(): void;

  abstract reset(): void;

  abstract resetSensorView(
    eventTrackingValueWithSource: EventTrackingValueWithSource,
  ): void;

  abstract setSelection(indexes: number[]): void;

  abstract undo(): void;

  abstract validateShape(
    shapeState: ShapeState<P, S>,
    isForSingleFrame: boolean,
  ): string | undefined;

  abstract loadTask(task: Task): Promise<LoadTaskResult>;

  abstract handleViewBoxUpdate(outputName: string, viewbox: ViewBox): void;

  abstract handleZoomPanelViewBoxUpdate(
    outputName: string,
    viewbox: ViewBox,
  ): void;

  abstract wasShapeCancelled(): boolean;

  abstract setTool(tool: ToolName, mode?: string): void;

  abstract cancelTool(eventTrackingValue: EventTrackingValue): void;

  abstract offsetShapesToMousePosition(shapes: ShapeState[]): ShapeState[];
}
